/**
 * Marketing Repository - Simplified for MVP
 * Handles: banners, categories, basic config
 * Removed: email campaigns, templates (not MVP critical)
 */

import { Banner, Category, SeoConfig, FlashDealConfig, MarketingConfig } from '../domain/types';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import {
collection,
getDocs,
getDoc,
doc,
addDoc,
deleteDoc,
setDoc,
onSnapshot
} from "firebase/firestore";
import { APP_CONFIG } from '../domain/constants';

const COLLECTIONS = {
  banners: 'banners',
  categories: 'categories',
  config: 'config'
} as const;

const CONFIG_DOC = 'marketing';

class MarketingRepository {
  // ===== BANNERS =====
  async getBanners(): Promise<Banner[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.banners));
      return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Banner));
    } catch (error) {
      logger.error('[Marketing] Error fetching banners', error);
      return [];
    }
  }

  async saveBanner(banner: Banner): Promise<void> {
    try {
      if (banner.id) {
        await setDoc(doc(db, COLLECTIONS.banners, banner.id), banner);
      } else {
        await addDoc(collection(db, COLLECTIONS.banners), banner);
      }
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Marketing] Error saving banner', error);
      throw error;
    }
  }

  async deleteBanner(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.banners, id));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Marketing] Error deleting banner', error);
      throw error;
    }
  }

  async deleteAllBanners(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.banners));
      await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Marketing] Error deleting all banners', error);
      throw error;
    }
  }

  // ===== CATEGORIES =====
  async getCategories(): Promise<Category[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.categories));
      return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Category));
    } catch (error) {
      logger.error('[Marketing] Error fetching categories', error);
      return [];
    }
  }

  async saveCategory(category: Category): Promise<void> {
    try {
      if (category.id) {
        await setDoc(doc(db, COLLECTIONS.categories, category.id), category);
      } else {
        await addDoc(collection(db, COLLECTIONS.categories), category);
      }
      CacheInvalidator.invalidateCategory();
    } catch (error) {
      logger.error('[Marketing] Error saving category', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.categories, id));
      CacheInvalidator.invalidateCategory();
    } catch (error) {
      logger.error('[Marketing] Error deleting category', error);
      throw error;
    }
  }

  async deleteAllCategories(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.categories));
      await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
      CacheInvalidator.invalidateCategory();
    } catch (error) {
      logger.error('[Marketing] Error deleting all categories', error);
      throw error;
    }
  }

  // ===== CONFIG =====
  subscribeToConfig(
    callback: (config: MarketingConfig) => void,
    onError?: (error: Error) => void
  ): () => void {
    try {
      return onSnapshot(
        doc(db, COLLECTIONS.config, CONFIG_DOC),
        (docSnap) => {
          if (docSnap.exists()) {
            callback(docSnap.data() as MarketingConfig);
          } else {
            // Return default config if doc doesn't exist
            callback({
              shippingRule: {
                minAmountForFreeShipping: APP_CONFIG.FREE_SHIPPING_THRESHOLD,
                freeShippingCategories: []
              },
              shippingCosts: { local: 1500, national: 5200 },
              seo: {
                siteTitle: 'AllTuc Marketplace',
                siteDescription: 'Diseño y Artesanías de Tucumán.'
              },
              flashDeals: {
                enabled: false,
                endTime: '',
                discountPercentage: 0,
                productIds: []
              },
              socialProof: {
                enabled: true,
                position: 'bottom-left',
                displayMode: 'inside',
                intervalMin: 8,
                intervalMax: 18,
                messages: ['{user} de {location} {action} {product}']
              },
              pickupAddress: 'San Martín 650, San Miguel de Tucumán'
            });
          }
        },
        (error) => {
          logger.error('[Marketing] Error subscribing to config', error);
          if (onError) onError(error as Error);
        }
      );
    } catch (error) {
      logger.error('[Marketing] Error setting up config subscription', error);
      if (onError) onError(error as Error);
      return () => {};
    }
  }

  async getConfig(): Promise<MarketingConfig> {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.config, CONFIG_DOC));
      if (docSnap.exists()) {
        return docSnap.data() as MarketingConfig;
      }
    } catch (error) {
      logger.error('[Marketing] Error fetching config', error);
    }

    // Default config
    return {
      shippingRule: {
        minAmountForFreeShipping: APP_CONFIG.FREE_SHIPPING_THRESHOLD,
        freeShippingCategories: []
      },
      shippingCosts: { local: 1500, national: 5200 },
      seo: { 
        siteTitle: 'AllTuc Marketplace', 
        siteDescription: 'Diseño y Artesanías de Tucumán.' 
      },
      flashDeals: { 
        enabled: false, 
        endTime: '', 
        discountPercentage: 0, 
        productIds: [] 
      },
      socialProof: {
        enabled: true,
        position: 'bottom-left',
        displayMode: 'inside',
        intervalMin: 8,
        intervalMax: 18,
        messages: ['{user} de {location} {action} {product}']
      },
      pickupAddress: 'San Martín 650, San Miguel de Tucumán'
    };
  }

  async updateConfig(updates: Partial<MarketingConfig>): Promise<void> {
    try {
      await setDoc(doc(db, COLLECTIONS.config, CONFIG_DOC), updates, { merge: true });
      CacheInvalidator.invalidateAll();
    } catch (error) {
      logger.error('[Marketing] Error updating config', error);
      throw error;
    }
  }
}

export const marketingRepository = new MarketingRepository();
